import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient, { formatApiError } from "../api";
import { Plus, Pencil, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const cfg = {
    draft: { label: "Concept", cls: "border-white/10 text-zinc-400" },
    published: { label: "Gepubliceerd", cls: "border-champagne/40 text-champagne" },
    hidden: { label: "Verborgen", cls: "border-white/10 text-zinc-500" },
  }[status] || { label: status, cls: "border-white/10 text-zinc-400" };
  return (
    <span
      className={`inline-flex px-3 py-1 text-[10px] tracking-[0.25em] uppercase border ${cfg.cls} font-body`}
    >
      {cfg.label}
    </span>
  );
};

const StoriesListPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/stories");
      setStories(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-stories-list">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
            CMS · Verhalen
          </p>
          <h1 className="font-display text-5xl text-white tracking-tight">
            Verhalen
          </h1>
          <p className="mt-3 text-sm text-zinc-400 font-body max-w-xl">
            Redactionele content. Concept-verhalen zijn niet zichtbaar op de
            site, gepubliceerde verhalen wel.
          </p>
        </div>
        <Link
          to="/admin/stories/new"
          data-testid="stories-new"
          className="inline-flex items-center gap-2 px-5 py-3 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold"
        >
          <Plus size={13} /> Nieuw verhaal
        </Link>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Laden...</div>
      ) : stories.length === 0 ? (
        <div className="bg-ink-900 border border-white/[0.06] p-10 text-center text-sm text-zinc-500 font-body">
          Nog geen verhalen aangemaakt.
        </div>
      ) : (
        <div className="bg-ink-900 border border-white/[0.06]">
          <div className="grid grid-cols-12 px-6 py-4 hairline-b text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
            <div className="col-span-5">Titel</div>
            <div className="col-span-2">Auteur</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Gepubliceerd</div>
            <div className="col-span-1 text-right">Acties</div>
          </div>
          {stories.map((s) => (
            <div
              key={s.id}
              data-testid={`admin-story-row-${s.slug}`}
              className="grid grid-cols-12 px-6 py-5 hairline-b last:border-b-0 items-center hover:bg-ink-850 transition-colors"
            >
              <div className="col-span-5">
                <p className="font-display text-xl text-white">{s.title}</p>
                <p className="text-xs text-zinc-500 font-body mt-1">/{s.slug}</p>
              </div>
              <div className="col-span-2 text-xs text-zinc-300 font-body">
                {s.author || "—"}
              </div>
              <div className="col-span-2">
                <StatusBadge status={s.status} />
              </div>
              <div className="col-span-2 text-xs text-zinc-400 font-body">
                {s.published_at
                  ? new Date(s.published_at).toLocaleDateString("nl-NL")
                  : "—"}
              </div>
              <div className="col-span-1 flex justify-end">
                <Link
                  to={`/admin/stories/${s.id}`}
                  data-testid={`admin-story-edit-${s.slug}`}
                  className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-champagne hover:text-champagne text-zinc-300 transition-colors"
                >
                  <Pencil size={13} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StoriesListPage;
